import {closeAddParameterDialog} from '@/modules/patients/components/patient-details/therapy/dialogs/util'
import {closeReversePatientArrivedDialog} from '@/modules/patients-therapies/components/reverse-patient-arrived-dialog/util'
import {useGetUser} from '@/modules/users/api'
import {usePrescriptionRoles} from '@/shared/hooks/use-permissions'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {
  PatientTherapySequenceEventType,
  PharmacistValidateEventBody,
  RemovablePatientTherapyValidation,
} from '@cypro/engine/mutators'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {canRemovePatientTherapyValidation, isInThePast} from '@cypro/engine/utils'
import {toaster} from '@cypro/ui/toaster'
import {useTranslate} from '@tolgee/react'
import {
  canApproveBudget,
  canDoctorValidate,
  canPharmacistValidate,
  canRejectBudget,
  useTherapySequenceActions,
} from './use-therapy-sequence-actions'

// Centralizes what the validation buttons can do and what they send:
// role/permission checks and the sendEvent/mutator + toast side effects live here,
// so the panels only decide which scenario to render.
export function useSequenceValidation(sequence: PatientTherapySequence) {
  const {t} = useTranslate('common')
  const {actor, sendEvent} = useTherapySequenceActions(sequence)
  const zero = useZero()
  const roles = usePrescriptionRoles()

  const isPendingBudgetApproval = sequence.status === PatientTherapySequenceStatus.enum.pending_approval
  const canDocValidate = canDoctorValidate(actor)
  const canPharmaValidate = canPharmacistValidate(actor)
  const canApprove = canApproveBudget(actor)
  const canReject = canRejectBudget(actor)
  // ward staff may undo the arrival until production starts (hidden, never disabled);
  // a past prescription is read-only, matching the footer actions
  const canReversePatientArrival =
    !isInThePast(sequence.therapyDate) &&
    actor.can({
      type: PatientTherapySequenceEventType.enum.REVERSE_PATIENT_ARRIVED,
    })

  const [doctorValidator] = useGetUser(sequence.doctorValidatedBy)
  const [pharmacistValidator] = useGetUser(sequence.pharmacistValidatedBy)

  function canRemoveValidation(validation?: RemovablePatientTherapyValidation): boolean {
    return canRemovePatientTherapyValidation(sequence, validation, roles)
  }

  // onRemoveValidation removes the latest validation: the pharmacist's once given, otherwise the doctor's
  const removeValidationLabel = sequence.pharmacistValidatedAt
    ? t('remove_pharmacist_validation')
    : t('remove_doctor_validation')

  async function doctorValidate(): Promise<boolean> {
    const res = await sendEvent({id: sequence.id, event: {type: 'DOCTOR_VALIDATE'}})
    if (res?.type === 'error') return false

    toaster.success({title: t('success'), description: t('therapy_sequence_doctor_validated_successfully')})
    return true
  }

  async function pharmacistValidate(body: PharmacistValidateEventBody): Promise<boolean> {
    const res = await sendEvent({id: sequence.id, event: {type: 'PHARMACIST_VALIDATE', body}})
    if (res?.type === 'error') return false

    toaster.success({title: t('success'), description: t('therapy_sequence_pharmacist_validated_successfully')})
    return true
  }

  async function removeValidation(validation?: RemovablePatientTherapyValidation): Promise<boolean> {
    const res = await zero.attemptMutation(
      zero.mutators.patientsTherapies.removePatientTherapyValidation({
        id: sequence.id,
        ...(validation ? {validation} : {}),
      }),
      async mutation => await mutation.server
    )
    if (res?.type === 'error') return false

    toaster.success({title: t('success'), description: t('validation_removed_successfully')})
    return true
  }

  async function approveBudget(): Promise<boolean> {
    const res = await sendEvent({id: sequence.id, event: {type: 'APPROVE_BUDGET'}})
    if (res?.type === 'error') return false

    toaster.success({title: t('success'), description: t('budget_approved_successfully')})
    return true
  }

  async function rejectBudget(): Promise<boolean> {
    const res = await sendEvent({id: sequence.id, event: {type: 'REJECT_BUDGET'}})
    if (res?.type === 'error') return false

    toaster.success({title: t('success'), description: t('budget_rejected_successfully')})
    return true
  }

  async function onApproveBudget() {
    const res = await approveBudget()
    if (!res) return
    closeAddParameterDialog()
  }

  async function onRejectBudget() {
    const res = await rejectBudget()
    if (!res) return
    closeAddParameterDialog()
  }

  async function onDoctorValidate() {
    const res = await doctorValidate()
    if (!res) return
    closeAddParameterDialog()
  }

  async function onPharmacistValidate(body: PharmacistValidateEventBody) {
    const res = await pharmacistValidate(body)
    if (!res) return
    closeAddParameterDialog()
  }

  // the prescription pop-up stays open so unsaved edits can be saved once the lock is gone
  async function onRemoveValidation() {
    await removeValidation()
  }

  // Confirmed reverse of the patient arrival; the triggering footer/card action only opens
  // the dialog (sendEvent intercepts the bodyless event).
  async function onReversePatientArrival() {
    const res = await sendEvent({
      id: sequence.id,
      event: {type: PatientTherapySequenceEventType.enum.REVERSE_PATIENT_ARRIVED, body: {}},
    })
    if (res?.type === 'error') return

    closeReversePatientArrivedDialog()
  }

  return {
    actor,
    isPendingBudgetApproval,
    canApproveBudget: canApprove,
    canRejectBudget: canReject,
    onApproveBudget,
    onRejectBudget,
    canDoctorValidate: canDocValidate,
    canPharmacistValidate: canPharmaValidate,
    canRemoveValidation,
    removeValidation,
    canReversePatientArrival,
    onReversePatientArrival,
    onDoctorValidate,
    onPharmacistValidate,
    onRemoveValidation,
    removeValidationLabel,
    doctorValidator,
    pharmacistValidator,
  }
}
